import { flattenScopeFieldResult } from "./scopeAnalysisDisplay";
import { fieldLabel } from "./analysis/analysisFieldMeta";
import { fieldIsOnScopeCard } from "./scopeAnalysisFields";

export const SOLUTION_FOR_TECH = {
  alteon: "adc",
  integrated_waf: "adc",
  cloud_waf: "appsec",
  api_security: "appsec",
  agentic_ai: "appsec",
  defensepro: "ddos",
  cloud_ddos: "ddos",
  cyber_controller: "management",
};

const SOLUTION_LABELS = {
  en: {
    alteon: "Alteon",
    integrated_waf: "On-Prem / Integrated WAF",
    cloud_waf: "Cloud WAF",
    api_security: "API Security",
    agentic_ai: "Agentic AI",
    defensepro: "DefensePro",
    cloud_ddos: "Cloud DDoS",
    cyber_controller: "Cyber Controller",
  },
  es: {
    alteon: "Alteon",
    integrated_waf: "WAF integrado / on-prem",
    cloud_waf: "Cloud WAF",
    api_security: "API Security",
    agentic_ai: "Agentic AI",
    defensepro: "DefensePro",
    cloud_ddos: "Cloud DDoS",
    cyber_controller: "Cyber Controller",
  },
  pt: {
    alteon: "Alteon",
    integrated_waf: "WAF integrado / on-prem",
    cloud_waf: "Cloud WAF",
    api_security: "API Security",
    agentic_ai: "Agentic AI",
    defensepro: "DefensePro",
    cloud_ddos: "Cloud DDoS",
    cyber_controller: "Cyber Controller",
  },
};

function emptyResult(status = "not_found") {
  return {
    status,
    value: null,
    evidence_row_ids: [],
    evidence_quality: "none",
    confidence: "low",
    notes: "",
    warnings: [],
  };
}

function foundResult(value, extra = {}) {
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) {
    return { ...emptyResult(), source_strategy: "synthesized_from_json", ...extra };
  }
  return {
    status: "found",
    value,
    evidence_row_ids: extra.evidence_row_ids || [],
    evidence_quality: extra.evidence_quality || "high",
    confidence: extra.confidence || "high",
    notes: extra.notes || "",
    warnings: [],
    source_strategy: "synthesized_from_json",
  };
}

function setNested(root, parts, value) {
  let node = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (!node[key] || typeof node[key] !== "object") node[key] = {};
    node = node[key];
  }
  node[parts[parts.length - 1]] = value;
}

function nestedPathForField(fieldId) {
  const parts = String(fieldId || "").split(".");
  if (parts[0] === "general") return ["general", parts[1]];
  if (parts[0] === "design") return ["design", ...parts.slice(1)];
  if (parts[0] === "sti") return ["support_training_implementation", ...parts.slice(1)];
  if (parts[0] === "commercial_admin") return ["commercial_administrative", ...parts.slice(1)];
  return null;
}

function techCards(cards) {
  return (cards || []).filter(
    (card) => (card.card_type === "technology" || card.card_type === "subtechnology") && card.technology_id
  );
}

function flattenFieldResult(fieldId, result, language) {
  return flattenScopeFieldResult(fieldId, result, language);
}

function flattenCards(cards, language = "en") {
  return (cards || []).map((card) => {
    const fields = Object.fromEntries(
      Object.entries(card.fields || {})
        .filter(([fieldId]) => fieldIsOnScopeCard(card, fieldId))
        .map(([fieldId, result]) => [
          fieldId,
          flattenFieldResult(fieldId, result, language),
        ])
    );
    const admin = card.administrative_access;
    if (!admin || typeof admin !== "object") return { ...card, fields };
    return {
      ...card,
      fields,
      administrative_access: {
        ...admin,
        fields: Object.fromEntries(
          Object.entries(admin.fields || {}).map(([fieldId, result]) => [
            fieldId,
            flattenFieldResult(fieldId, result, language),
          ])
        ),
      },
    };
  });
}

export function buildScopeSolutions(cards, language = "en") {
  const tech = techCards(cards);
  const labels = SOLUTION_LABELS[language] || SOLUTION_LABELS.en;
  return tech
    .filter((card) => card.scope_role !== "subtechnology" && card.card_type !== "subtechnology")
    .map((card) => {
      const cardId = card.card_id || "";
      const techId = card.technology_id || "";
      const childIds = tech
        .filter((child) => child.parent_id === cardId || child.parent_id === `technology:${techId}`)
        .map((child) => child.card_id)
        .filter(Boolean);
      return {
        id: cardId || `technology:${techId}`,
        technology_id: techId,
        label: labels[techId] || card.title || techId,
        card_ids: [cardId, ...childIds].filter(Boolean),
        testId: `design-solution-${techId}`,
      };
    });
}

function nestCardFields(cards) {
  const nested = {
    design: {
      ddos: { cloud: {}, onprem: {} },
      alteon: {},
      application_security: {},
      global_management: {},
      design_gaps: emptyResult(),
    },
    support_training_implementation: {
      support: {},
      training: {},
      implementation: {},
      sti_gaps: emptyResult(),
    },
    commercial_administrative: {
      commercial_admin_gaps: emptyResult(),
    },
  };
  (cards || []).forEach((card) => {
    if (card.card_type === "administrative_access") return;
    Object.entries(card.fields || {}).forEach(([fieldId, result]) => {
      const path = nestedPathForField(fieldId);
      if (!path || !result || typeof result !== "object") return;
      setNested(nested, path, result);
    });
  });
  return nested;
}

function qualitySummaryFromCards(cards) {
  const summary = {
    total_fields: 0,
    found: 0,
    not_found: 0,
    not_applicable: 0,
    ambiguous: 0,
    inferred: 0,
    high_confidence: 0,
    medium_confidence: 0,
    low_confidence: 0,
  };
  (cards || []).forEach((card) => {
    if (card.card_type === "administrative_access") return;
    Object.values(card.fields || {}).forEach((result) => {
      if (!result || typeof result.status !== "string") return;
      summary.total_fields += 1;
      if (summary[result.status] != null) summary[result.status] += 1;
      if (result.confidence === "high") summary.high_confidence += 1;
      if (result.confidence === "medium") summary.medium_confidence += 1;
      if (result.confidence === "low") summary.low_confidence += 1;
    });
  });
  return summary;
}

function gapItems(cards, prefixes) {
  const items = [];
  const seen = new Set();
  (cards || []).forEach((card) => {
    if (card.card_type === "administrative_access") return;
    Object.entries(card.fields || {}).forEach(([fieldId, result]) => {
      if (!prefixes.some((prefix) => fieldId.startsWith(prefix))) return;
      if (!fieldIsOnScopeCard(card, fieldId)) return;
      if (result?.status !== "not_found" && result?.status !== "ambiguous") return;
      const label = fieldLabel(fieldId);
      const text =
        result.status === "not_found"
          ? `${label}: information not found in the approved pages.`
          : `${label}: evidence is ambiguous.`;
      if (seen.has(text)) return;
      seen.add(text);
      items.push(text);
    });
  });
  return items.slice(0, 12);
}

function productPresenceFromCards(cards) {
  const ids = new Set(techCards(cards).map((card) => card.technology_id));
  return {
    alteon: ids.has("alteon"),
    integrated_waf: ids.has("integrated_waf"),
    cloud_waf: ids.has("cloud_waf"),
    application_security: ["integrated_waf", "cloud_waf", "api_security", "agentic_ai"].some((id) => ids.has(id)),
    adc_embedded_appsec: false,
    ddos_onprem: ids.has("defensepro"),
    ddos_cloud: ids.has("cloud_ddos"),
    cyber_controller: ids.has("cyber_controller"),
    api_security: ids.has("api_security"),
    agentic_ai: ids.has("agentic_ai"),
  };
}

function productAdminFromCards(cards) {
  return techCards(cards)
    .filter((card) => card.administrative_access)
    .map((card) => ({
      technology_id: card.technology_id,
      title: card.administrative_access.title || card.title,
      solution_id: card.administrative_access.solution_id || SOLUTION_FOR_TECH[card.technology_id],
      items: card.administrative_access.items || [],
      fields: card.administrative_access.fields || {},
    }));
}

function buildOverview(cards, analysis) {
  if (analysis?.general?.executive_summary) return analysis.general;
  const names = techCards(cards).map((card) => card.title).filter(Boolean);
  const unique = [...new Set(names)];
  const foundBits = techCards(cards).map((card) => {
    const values = Object.values(card.fields || {})
      .filter((result) => result?.status === "found" && result.value != null && result.value !== "")
      .map((result) => String(result.value));
    return values.length ? `${card.title} includes ${values.slice(0, 4).join(", ")}.` : "";
  }).filter(Boolean);
  const paragraphs = [
    unique.length ? `This RFP covers ${unique.join(", ")} in the approved RFP Discovery Scope.` : "This RFP Discovery Scope Analysis is available.",
    ...foundBits.slice(0, 2),
  ];
  return {
    customer_name: foundResult(null),
    technologies_involved: foundResult(unique),
    project_scope: foundResult(null),
    important_aspects: foundResult(null),
    missing_key_information: foundResult(gapItems(cards, ["design.", "sti.", "commercial_admin."]).slice(0, 8)),
    executive_summary: foundResult(paragraphs),
  };
}

export function assembleScopeAnalysis(analysis) {
  if (!analysis) return analysis;
  const language = analysis.language || "en";
  const cards = flattenCards(analysis.cards || [], language);
  const withCards = { ...analysis, cards };
  const scopeSolutions =
    withCards.scope_solutions?.length ? withCards.scope_solutions : buildScopeSolutions(cards, language);
  if (withCards.general?.executive_summary && withCards.design && withCards.product_presence) {
    return { ...withCards, language, scope_solutions: scopeSolutions };
  }
  const nested = nestCardFields(cards);
  const designGaps = gapItems(cards, ["design."]);
  const stiGaps = gapItems(cards, ["sti."]);
  const commercialGaps = gapItems(cards, ["commercial_admin."]);
  return {
    ...withCards,
    language,
    general: buildOverview(cards, withCards),
    design: {
      ...nested.design,
      design_gaps: designGaps.length ? foundResult(designGaps) : nested.design.design_gaps,
    },
    support_training_implementation: {
      ...nested.support_training_implementation,
      sti_gaps: stiGaps.length ? foundResult(stiGaps) : nested.support_training_implementation.sti_gaps,
    },
    commercial_administrative: {
      ...nested.commercial_administrative,
      commercial_admin_gaps: commercialGaps.length
        ? foundResult(commercialGaps)
        : nested.commercial_administrative.commercial_admin_gaps,
    },
    product_presence: withCards.product_presence || productPresenceFromCards(cards),
    product_administrative_access: withCards.product_administrative_access || productAdminFromCards(cards),
    scope_solutions: scopeSolutions,
    quality_summary: withCards.quality_summary || qualitySummaryFromCards(cards),
    warnings: withCards.warnings || [],
  };
}
